import { gql } from '@apollo/client';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { getServerFrontendConfig } from '~/jobiqo/lib/frontendConfig';
import { isModuleEnabled } from '~/jobiqo/lib/config';

const getRuntimeFrontendConfig = (frontendConfig = null) => {
  if (frontendConfig) {
    return frontendConfig;
  }

  return typeof window === 'undefined'
    ? getServerFrontendConfig()
    : getCurrentFrontendConfig();
};

/**
 * @param {object | null} frontendConfig
 * @param {string[] | null} enabledModules
 */
export const getJobFragment = (
  frontendConfig = null,
  enabledModules = null,
) => {
  const jobsConfig = getRuntimeFrontendConfig(frontendConfig).JOBS || {};
  const sourceEnabled = jobsConfig.ANALYTICS?.SOURCE_ENABLED;
  const moduleEnabled = (name) =>
    enabledModules ? enabledModules.includes(name) : isModuleEnabled(name);
  const salaryRangeFreeInputEnabled = moduleEnabled(
    'jobiqo_job_salary_range_free_input',
  );
  const seasonalJobsEnabled = jobsConfig.SEASONAL_JOBS_ENABLED;

  return gql`
  fragment JobFragment on Job {
    id
    ownerId
    title
    canEdit
    canRemove
    organization
    organizationProfile {
      organization {
        id
      }
      name
      url {
        path
      }
      urlNoPrefix
      logo(style: "job_squared_logo_medium") {
        url
        alt
      }
      workflowState {
        value
        label
      }
      isPremium
      metatags {
        meta
        value
      }
    }
    description
    type
    url {
      path
    }
    urlNoPrefix
    linkUrl
    file
    logo(style: "job_squared_logo_medium") {
      url
    }
    expiration
    pause
    duration
    published
    formattedOriginalPublished
    formattedPublished
    formattedExpiration
    formattedPause
    workflowState {
      value
      label
    }
    employmentType {
      tid
      label
      urlAlias
    }
    fieldsOfStudy {
      tid
      label
    }
    occupationalField {
      tid
      label
      urlAlias
    }
    address
    administrativeArea
    region {
      tid
      label
      urlAlias
    }
    degreeLevel {
      tid
      label
    }
    languages {
      tid
      label
    }
    skills {
      tid
      label
    }
    salaryRange {
      tid
      label
    }
    absoluteUrl
    absoluteOrgUrl
    ${
      salaryRangeFreeInputEnabled
        ? `
      salaryRangeFree {
        minSalary
        maxSalary
        currencyCode
        salaryUnit
      }
    `
        : ''
    }
    yearsOfExperience {
      tid
      label
    }
    isFeatured
    isHighlight
    isTopJob
    applicationWorkflow
    applicationEmails
    applicationLink
    applicationRestricted {
      restrictionStatus
      restrictionMessage
    }
    location {
      administrativeArea
      countryCode
      locality
      addressLine1
      addressLine2
      zip
    }
    jobUpgrades
    ${moduleEnabled('jobiqo_atnet') ? 'networkJobId' : ''}
    bookmarked
    latitude
    longitude
    referenceId
    schemaOrgSalary {
      defaultCurrency
      exactValue {
        number
      }
      minValue {
        number
      }
      maxValue {
        number
      }
      unitText
    }
    schemaOrgEmployment
    schemaOrgJobLocationType
    ${sourceEnabled ? 'source' : ''}
    isExcluded
    applicationRequireCv
    applicationRequireCoverLetter
    applicationRequireQualifyingQuestions
    applicationQualifyingQuestions {
      questionId
      question
      required
    }
    ${
      seasonalJobsEnabled
        ? `
      availabilityPeriod {
        startDate
        endDate
      }
    `
        : ''
    }
    videoUrl
    formattedFrontends
    frontends
    jobPostingFrontend
    analyticsJobProductType
  }
`;
};

export default getJobFragment;
